import { Component, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { AbstractStepConfigComponent } from '../abstract-step-config/abstract-step-config.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { AbstractStepDTO, DelayStepDTO } from '@tj/tj-dmp-lib-api';
import { ON_CANCEL_TOKEN, ON_SAVE_TOKEN, PARTNER_TOKEN, READ_ONLY_MODE_TOKEN, STEP_TOKEN, TENANT_TOKEN } from '../injection-tokens';
import { takeUntil } from 'rxjs/operators';
import { StepDescriptionService } from '../step-description.service';

@Component({
    selector: 'dmp-delay-step-config',
    templateUrl: './delay-step-config.component.html',
    styleUrls: ['./delay-step-config.component.scss'],
    standalone: false
})
export class DelayStepConfigComponent extends AbstractStepConfigComponent {
  private readonly stepDescriptionService = inject(StepDescriptionService);

  delayForm: FormGroup;
  
  get delayStep(): DelayStepDTO {
    return this.step as DelayStepDTO;
  }

  constructor(
    @Inject(STEP_TOKEN) step: AbstractStepDTO,
    @Inject(TENANT_TOKEN) tenant: string,
    @Inject(PARTNER_TOKEN) partner: string,
    @Inject(READ_ONLY_MODE_TOKEN) readOnlyMode: string,
    @Inject(ON_SAVE_TOKEN) onSaveCallback: () => void,
    @Inject(ON_CANCEL_TOKEN) onCancelCallback: () => void,
    private formBuilder: FormBuilder
  ) {
    super(step, tenant, partner, readOnlyMode, onSaveCallback, onCancelCallback);
  }

  protected initializeComponent(): void {
    this.createForm();
    this.initializeForm();
  }

  protected updateStepFromForm(): void {
    if (!this.delayStep || !this.delayForm.valid) return;

    const { days, hours, minutes } = this.delayForm.value;

    this.delayStep.description = this.generateReadableDescription(days, hours, minutes);

    this.delayStep.days = days || undefined;
    this.delayStep.hours = hours || undefined;
    this.delayStep.minutes = minutes || undefined;
  }

  protected isConfigurationValid(): boolean {
    if (!this.delayForm.valid) return false;

    const { days, hours, minutes } = this.delayForm.value;
    
    // Au moins une valeur doit être définie et supérieure à 0
    return (days && days > 0) || (hours && hours > 0) || (minutes && minutes > 0);
  }

  private createForm(): void {
    this.delayForm = this.formBuilder.group({
      days: this.createFormControl(0, [Validators.min(0), Validators.max(365)]),
      hours:this.createFormControl(0, [Validators.min(0), Validators.max(23)]),
      minutes:this.createFormControl(0, [Validators.min(0), Validators.max(59)])
    });

    // Surveiller les changements pour marquer comme modifié
    this.delayForm.valueChanges.pipe(
      takeUntil(this.destroy$),
    ).subscribe(() => {
      if (!this.isInitializing) {
        this.markAsChanged()
      }
    });
  }

  private initializeForm(): void {
    if (!this.delayStep) return;

    this.isInitializing = true;

    this.delayForm.patchValue({
      days: this.delayStep.days || 0,
      hours: this.delayStep.hours || 0,
      minutes: this.delayStep.minutes || 0
    });

    this.isInitializing = false;
  }


  setPresetDelay(days: number = 0, hours: number = 0, minutes: number = 0): void {
    this.delayForm.patchValue({ days, hours, minutes });
  }

  private generateReadableDescription(days: number, hours: number, minutes: number): string {
    return this.stepDescriptionService.delay(days, hours, minutes);
  }
}
